import { computed, inject, type ComputedRef, type Ref } from 'vue'

import useCanvas from '@canvas-builder/composables/useCanvas'
import { ComponentTypes, type ComponentNode } from '@canvas-builder/types/canvas'
import { findParentByNodeId } from '@canvas-builder/util/tree'
import { type CanvasRetrieve } from '@openapi'
import type { PitcherInfo } from '@lib/types/app'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'

function calculateCanvasPermissions(id: ComponentNode['id'], isAnyTypeOfAdmin: boolean) {
  const { componentNodesById } = useCanvas()

  const componentNode = componentNodesById.value[id] ?? null

  if (componentNode?.type === ComponentTypes.DynamicDataTable) {
    // do not show edit option in dynamic data table if user is not an admin
    return { hasSomethingEditable: true, canEdit: isAnyTypeOfAdmin, canRemove: true, canDuplicate: true }
  }

  if (componentNode?.type === ComponentTypes.CustomDataTable) {
    // do not show edit option in custom data table if user is not an admin
    return { hasSomethingEditable: true, canEdit: isAnyTypeOfAdmin, canRemove: true, canDuplicate: true }
  }

  return { hasSomethingEditable: true, canEdit: true, canRemove: true, canDuplicate: true }
}

export default function ({
  id,
  usedInSectionId,
  when_used_in_section,
}: {
  id: Ref<ComponentNode['id']>
  usedInSectionId: Ref<CanvasRetrieve['id']>
  when_used_in_section: Ref<ComponentNode['when_used_in_section']>
}) {
  const pitcherInfo = inject<ComputedRef<PitcherInfo>>(
    'pitcherInfo',
    computed(() => ({
      accessToken: '',
      instanceId: '',
      apiOrigin: '',
      isAnyTypeOfAdmin: false,
    })),
  )

  const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
    'launchDarkly',
    computed(() => ({})),
  )

  const { canvasContent, activeCanvas } = useCanvas()

  return computed(() => {
    // If used in a section, apply section permissions (existing behavior)
    if (usedInSectionId.value) {
      const parent = findParentByNodeId(canvasContent.value, id.value)
      const canRemove = !!when_used_in_section.value?.is_removable
      const canEdit = !!when_used_in_section.value?.is_editable
      const canDuplicate = parent ? !!parent.when_used_in_section?.is_editable : false

      return {
        hasSomethingEditable: canEdit || canRemove || canDuplicate,
        canRemove,
        canEdit,
        canDuplicate,
      }
    }

    // If canvas was created from a template and feature flag is enabled, apply template permissions
    // Only apply if permissions were explicitly set (object exists with defined properties)
    const hasExplicitPermissions =
      when_used_in_section.value !== null &&
      when_used_in_section.value !== undefined &&
      (when_used_in_section.value.is_editable !== undefined || when_used_in_section.value.is_removable !== undefined)
    if (
      activeCanvas.value?.template?.id &&
      launchDarkly.value.enable_template_component_permissions &&
      hasExplicitPermissions
    ) {
      const canRemove = !!when_used_in_section.value?.is_removable
      const canEdit = !!when_used_in_section.value?.is_editable
      // For template permissions, duplication follows editability (no parent inheritance)
      const canDuplicate = canEdit

      return {
        hasSomethingEditable: canEdit || canRemove || canDuplicate,
        canRemove,
        canEdit,
        canDuplicate,
      }
    }

    // Default permissions
    return calculateCanvasPermissions(id.value, !!pitcherInfo.value?.isAnyTypeOfAdmin)
  })
}
